/**
 * CEFR Statistics Card Component
 * Displays CEFR knowledge point statistics for a course with expandable details
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cefrKnowledgeService, CourseKnowledgePointStats, CEFRLevel, KnowledgePoint } from '../../services/cefrKnowledgeService';
import './CEFRStatsCard.css';

interface CEFRStatsCardProps {
  courseId: string;
  className?: string;
}

const CEFR_LEVELS: CEFRLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export const CEFRStatsCard: React.FC<CEFRStatsCardProps> = ({ courseId, className }) => {
  const { t } = useTranslation('course');
  const [stats, setStats] = useState<CourseKnowledgePointStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await cefrKnowledgeService.getCourseKnowledgePointStats(courseId);
        if (isMounted) {
          setStats(data);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to fetch CEFR stats:', err);
          setError('Failed to load knowledge points');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (courseId) {
      fetchStats();
    }
    return () => { isMounted = false; };
  }, [courseId]);

  if (loading) {
    return (
      <div className={`cefr-stats-card cefr-stats-card--loading ${className || ''}`}>
        <div className="cefr-stats-card__loader" />
      </div>
    );
  }

  if (error || !stats) {
    return null; // Don't show the card if there's an error
  }

  if (stats.total === 0) {
    return (
      <div className={`cefr-stats-card cefr-stats-card--empty ${className || ''}`}>
        <div className="cefr-stats-card__empty-icon">📚</div>
        <div className="cefr-stats-card__empty-text">{t('cefr.noKnowledgePoints')}</div>
      </div>
    );
  }

  // Calculate max count for bar width percentage
  const maxCount = Math.max(...Object.values(stats.byLevel));

  // Group knowledge points by category
  const vocabularyItems = stats.knowledgePoints.filter(kp => kp.category === 'vocabulary');
  const phrasalVerbItems = stats.knowledgePoints.filter(kp => kp.category === 'phrasal_verb');
  const grammarItems = stats.knowledgePoints.filter(kp => kp.category === 'grammar');

  const renderKnowledgePointItem = (kp: KnowledgePoint) => (
    <div key={kp.id} className="cefr-kp-item">
      <span className={`cefr-level-badge cefr-level-badge--${kp.cefrLevel} cefr-level-badge--small`}>
        {kp.cefrLevel}
      </span>
      <span className="cefr-kp-item__term">{kp.term}</span>
      {kp.definitions?.['zh-TW'] && (
        <span className="cefr-kp-item__def">{kp.definitions['zh-TW']}</span>
      )}
    </div>
  );

  return (
    <div className={`cefr-stats-card ${isExpanded ? 'cefr-stats-card--expanded' : ''} ${className || ''}`}>
      <div
        className="cefr-stats-card__header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <span className="cefr-stats-card__icon">📊</span>
        <h3 className="cefr-stats-card__title">{t('cefr.knowledgePoints')}</h3>
        <span className="cefr-stats-card__total">{t('cefr.total', { count: stats.total })}</span>
        <span className={`cefr-stats-card__toggle ${isExpanded ? 'cefr-stats-card__toggle--expanded' : ''}`}>
          ▼
        </span>
      </div>

      <div className="cefr-stats-card__levels">
        {CEFR_LEVELS.map((level) => {
          const count = stats.byLevel[level] || 0;
          const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;

          return (
            <div key={level} className="cefr-level-item">
              <div className="cefr-level-item__header">
                <span className={`cefr-level-badge cefr-level-badge--${level}`}>
                  {level}
                </span>
                <span className="cefr-level-item__count">{count}</span>
              </div>
              <div className="cefr-level-item__bar">
                <div
                  className={`cefr-level-item__bar-fill cefr-level-item__bar-fill--${level}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="cefr-stats-card__categories">
        <div className="cefr-category-item">
          <span className="cefr-category-item__icon">📚</span>
          <span>{t('cefr.vocabulary')}</span>
          <span className="cefr-category-item__count">{stats.byCategory.vocabulary}</span>
        </div>
        <div className="cefr-category-item">
          <span className="cefr-category-item__icon">📝</span>
          <span>{t('cefr.phrasalVerbs')}</span>
          <span className="cefr-category-item__count">{stats.byCategory.phrasal_verb}</span>
        </div>
        <div className="cefr-category-item">
          <span className="cefr-category-item__icon">📖</span>
          <span>{t('cefr.grammar')}</span>
          <span className="cefr-category-item__count">{stats.byCategory.grammar}</span>
        </div>
      </div>

      {/* Expandable Knowledge Points List */}
      {isExpanded && (
        <div className="cefr-stats-card__details">
          {vocabularyItems.length > 0 && (
            <div className="cefr-kp-section">
              <h4 className="cefr-kp-section__title">
                <span>📚</span> {t('cefr.vocabularySection', { count: vocabularyItems.length })}
              </h4>
              <div className="cefr-kp-section__list">
                {vocabularyItems.map(renderKnowledgePointItem)}
              </div>
            </div>
          )}

          {phrasalVerbItems.length > 0 && (
            <div className="cefr-kp-section">
              <h4 className="cefr-kp-section__title">
                <span>📝</span> {t('cefr.phrasalVerbsSection', { count: phrasalVerbItems.length })}
              </h4>
              <div className="cefr-kp-section__list">
                {phrasalVerbItems.map(renderKnowledgePointItem)}
              </div>
            </div>
          )}

          {grammarItems.length > 0 && (
            <div className="cefr-kp-section">
              <h4 className="cefr-kp-section__title">
                <span>📖</span> {t('cefr.grammarSection', { count: grammarItems.length })}
              </h4>
              <div className="cefr-kp-section__list">
                {grammarItems.map(renderKnowledgePointItem)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CEFRStatsCard;
